import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { links } from '@/utils/link'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { type CopyJobResponse } from '@/features/jobs/JobStore'
import type { NavigateFn } from '@/RootStore'
import { Id } from '@/infra/id'

/**
 * View model for the dialog that copies a job.
 */
export class CopyJobDialogViewModel {
  readonly modal = new ModalViewModel()

  constructor(
    private readonly _copyJob: () => Promise<CopyJobResponse>,
    private readonly _navigate: NavigateFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Copies the current job and opens the copy.
   */
  copyJob = task.resolved(async () => {
    const alert = this._newAlert()
      .show()
      .withState('pending')
      .saying('Copying job...')

    const copiedJob = await this._copyJob().catch((err: unknown) => {
      alert.dismiss()
      return this._createAlertErrorHandler('Copying the job failed')(err)
    })
    if (copiedJob.readyForSubmission) {
      alert
        .withState('success')
        .saying('Job copied! Please review the job and submit it.')
        .autoDismiss()
    } else {
      alert
        .withState('warning')
        .saying(
          'Job copied though some questions have changed! Please review the job before attempting to submit it.',
        )
        .autoDismiss()
    }

    this._navigate(links.job(Id.assert(copiedJob.jobId)))
  })
}
